import { useCallback, useEffect, useState } from "react";
import { guestPortalAuthApi } from "../api";
import {
  getRealtimeHubConnection,
  startRealtimeHubConnection,
} from "@/utils/realtimeHub.js";
import {
  clearGuestSession,
  getStoredGuestSession,
  isStoredGuestSessionFresh,
  normalizeGuestSession,
  saveGuestSession,
} from "@/utils/guestSessionStorage.js";

let cookieSessionPromise = null;

const hydrateRoom = (session) => {
  if (session?.room) {
    return {
      id: session.room.id || session.room.Id || session.roomId,
      name: session.room.name || session.room.Name || session.roomName,
      roomNumber: session.room.name || session.room.Name || session.roomName,
      unitType: session.room.unitType || session.room.UnitType || "",
      floor: session.room.floor || session.room.Floor || "",
      status: session.room.status || session.room.Status || "",
    };
  }

  if (!session?.roomName) return null;

  return {
    id: session.roomId,
    name: session.roomName,
    roomNumber: session.roomName,
  };
};

const hydrateHotel = (session) => {
  if (!session?.hotelName && !session?.organizationName) return null;

  return {
    id: session.organizationId,
    name: session.hotelName || session.organizationName,
    location: session.hotelLocation || null,
    category: session.hotelCategory || null,
  };
};

export const useSession = () => {
  const [session, setSession] = useState(() => getStoredGuestSession());
  const [hotel, setHotel] = useState(() => hydrateHotel(getStoredGuestSession()));
  const [room, setRoom] = useState(() => hydrateRoom(getStoredGuestSession()));
  const [loading, setLoading] = useState(() => !getStoredGuestSession());
  const [error, setError] = useState(null);

  const loadCookieSession = useCallback(async () => {
    cookieSessionPromise ??= guestPortalAuthApi.me()
      .then((response) => {
        const result = response?.data;
        const sessionData = result?.data || result;
        return normalizeGuestSession(sessionData);
      })
      .finally(() => {
        cookieSessionPromise = null;
      });

    const normalizedSession = await cookieSessionPromise;

    saveGuestSession(normalizedSession);
    setSession(normalizedSession);
    setRoom(hydrateRoom(normalizedSession));
    setHotel(hydrateHotel(normalizedSession));
    setLoading(false);
    return normalizedSession;
  }, []);

  const expireSession = useCallback((message = "Guest session is no longer active") => {
    clearGuestSession();
    setSession(null);
    setRoom(null);
    setHotel(null);
    setError(message);

    if (!window.location.pathname.includes("/guest/expired")) {
      window.location.replace("/guest/expired");
    }
  }, []);


  useEffect(() => {
    const checkinGuestSession = async () => {
      try {
        setLoading((current) => current || !getStoredGuestSession());
        setError(null);

        const storedSession = getStoredGuestSession();
        if (storedSession?.isActive && isStoredGuestSessionFresh()) {
          setSession(storedSession);
          setRoom(hydrateRoom(storedSession));
          setHotel(hydrateHotel(storedSession));
          setLoading(false);
          return;
        }

        await loadCookieSession();
      } catch (err) {
        console.error("Guest checkin error:", err);

        setSession(null);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to check in guest session"
        );
        setLoading(false);
      }
    };

    checkinGuestSession();
  }, [loadCookieSession]);

  useEffect(() => {
    if (!session?.isActive) return;

    const heartbeat = () => {
      guestPortalAuthApi.heartbeat().catch((error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          expireSession("Guest session has expired.");
        }
      });
    };

    heartbeat();
    const interval = window.setInterval(heartbeat, 45000);

    return () => window.clearInterval(interval);
  }, [expireSession, session?.isActive, session?.id, session?.sessionId]);

  useEffect(() => {
    if (!session?.isActive) return;

    const sessionId = session.sessionId || session.id;
    if (!sessionId) return;

    const hubOptions = {
      guestSessionId: sessionId,
    };
    const connection = getRealtimeHubConnection(hubOptions);

    const handleExpired = (payload = {}) => {
      const payloadSessionId =
        payload.sessionId || payload.SessionId || payload.id || payload.Id;

      if (sessionId && payloadSessionId && payloadSessionId === sessionId) {
        expireSession("Guest session has been checked out.");
      }
    };

    const handleExtended = (payload = {}) => {
      const payloadSessionId =
        payload.sessionId || payload.SessionId || payload.id || payload.Id;

      if (
        !sessionId ||
        !payloadSessionId ||
        payloadSessionId !== sessionId
      ) {
        return;
      }

      setSession((current) => {
        if (!current) return current;

        const updated = normalizeGuestSession({ ...current, ...payload });
        saveGuestSession(updated);
        return updated;
      });
    };

    const handleForceLogout = () => expireSession("Guest session has been checked out.");

    connection.on("ForceLogout", handleForceLogout);
    connection.on("GuestSessionCheckedOut", handleExpired);
    connection.on("GuestSessionDeleted", handleExpired);
    connection.on("GuestSessionExtended", handleExtended);

    startRealtimeHubConnection(hubOptions).catch(() => {
      // The periodic API validation still protects the session if realtime fails.
    });

    return () => {
      connection.off("ForceLogout", handleForceLogout);
      connection.off("GuestSessionCheckedOut", handleExpired);
      connection.off("GuestSessionDeleted", handleExpired);
      connection.off("GuestSessionExtended", handleExtended);
    };
  }, [expireSession, session?.id, session?.sessionId, session?.isActive]);

  return { session, hotel, room, loading, error };
};
